import { useEffect, useState } from "react";
import { recall, remember } from "@/lib/newsCache";
import { CloudSun, MapPin, Volume2, Square, Loader2, Radio, CalendarRange, Newspaper, Sunrise, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSpeaker } from "@/components/home/useSpeaker";
import { TomorrowSky } from "@/components/home/TomorrowSky";
import { WeekendSlot } from "@/components/home/WeekendSlot";
import { BriefHistory } from "@/components/home/BriefHistory";
import { NewsPanel } from "@/components/home/NewsPanel";
import { OvernightCard } from "@/components/home/OvernightCard";
import { PrefsPanel } from "@/components/home/PrefsPanel";
import { DigestPanel } from "@/components/home/DigestPanel";
import { EveningCard } from "@/components/home/EveningCard";
import { RecapCard } from "@/components/home/RecapCard";

const shortDay = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" });

export const HomeWeather = () => {
  const { languages, lang } = useI18n();
  const [data, setData] = useState(null);
  const [news, setNews] = useState(null);
  const [panel, setPanel] = useState(() => localStorage.getItem("chatmilu.home.panel") || "");
  const [weekend, setWeekend] = useState(null);
  const [moving, setMoving] = useState(false);
  const [planning, setPlanning] = useState("");
  const [voice, setVoice] = useState(() => localStorage.getItem("chatmilu.brief.voice") || "nova");
  const [briefLang, setBriefLang] = useState(() => localStorage.getItem("chatmilu.brief.lang") || "");
  const [offerBrief, setOfferBrief] = useState(false);
  const speaker = useSpeaker(voice);
  const sundayEvening = new Date().getDay() === 0 && new Date().getHours() >= 17;

  const load = () => api.get("/home/weather").then(({ data: d }) => { setData(d); remember("home:weather", d); });
  const loadNews = () => api.get("/home/news").then(({ data: d }) => { setNews(d); remember("home:news", d); });
  const loadWeekend = () => api.get("/calendar/weekend").then(({ data: d }) => setWeekend(d));

  useEffect(() => {
    const grumble = (e) => console.error("home card could not load something", e);
    const sky = recall("home:weather");
    const paper = recall("home:news");
    if (sky) setData(sky); else load().catch(grumble);
    if (paper) setNews(paper); else loadNews().catch(grumble);
    loadWeekend().catch(grumble);
    const now = new Date();
    if (now.getHours() >= 5 && now.getHours() < 12
        && localStorage.getItem("chatmilu.brief.offered") !== now.toISOString().slice(0, 10)) {
      setOfferBrief(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickLanguage = (code) => {
    setBriefLang(code);
    localStorage.setItem("chatmilu.brief.lang", code);
  };

  const pickVoice = (id) => {
    setVoice(id);
    localStorage.setItem("chatmilu.brief.voice", id);
  };

  const dismissOffer = () => {
    localStorage.setItem("chatmilu.brief.offered", new Date().toISOString().slice(0, 10));
    setOfferBrief(false);
  };

  const speakBrief = (path) =>
    speaker.play(path, async () => (await api.get(path, { params: { lang: briefLang || lang } })).data.text);

  const moveIt = async (plan) => {
    setMoving(true);
    try {
      await api.patch(`/calendar/events/${plan.event_id}/day`, null,
                      { params: { day: plan.better.day } });
      toast.success(`${plan.title} moved to the drier day`);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not move that plan");
    } finally {
      setMoving(false);
    }
  };

  // One tap turns a dry weekend window into a real plan.
  const planIt = async (slot) => {
    setPlanning(`${slot.day}-${slot.from}`);
    try {
      await api.post("/calendar/events", {
        title: "Time outdoors", day: slot.day, start: slot.from, end: slot.to,
        all_day: false, outdoors: true, colour: "green",
        notes: `MiLu picked this window — ${slot.hours}h under a 30% chance of rain.`,
      });
      toast.success(`Planned for ${shortDay(slot.day)} ${slot.from} – ${slot.to}`);
      await Promise.all([load(), loadWeekend()]);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not add that plan");
    } finally {
      setPlanning("");
    }
  };

  if (!data) return null;

  const BriefButton = ({ path, label, icon: Icon, primary, testid }) => (
    <button
      data-testid={testid}
      onClick={() => speakBrief(path)}
      disabled={Boolean(speaker.loading)}
      className={`inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-semibold transition-colors active:scale-95 disabled:opacity-50 sm:min-h-[38px] sm:flex-none sm:rounded-full sm:text-xs ${
        primary ? "bg-brand text-white hover:bg-brand-hover"
                : "border border-brand/40 text-brand hover:bg-brand/10"
      }`}
    >
      {speaker.loading === path ? <Loader2 className="h-4 w-4 animate-spin" />
        : speaker.playing === path ? <Square className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
      {speaker.playing === path ? "Stop" : label}
    </button>
  );

  const show = (which) =>
    setPanel((now) => {
      const next = now === which ? "" : which;
      localStorage.setItem("chatmilu.home.panel", next);
      return next;
    });

  const ShowButton = ({ label, icon: Icon, on, onClick, testid }) => (    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-semibold transition-colors active:scale-95 sm:min-h-[38px] sm:flex-none sm:rounded-full sm:text-xs ${
        on ? "bg-brand/15 text-brand ring-1 ring-brand/50"
           : "border border-brand/40 text-brand hover:bg-brand/10"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div data-testid="home-weather" className="mt-8 rounded-3xl border border-white/10 bg-panel p-5 sm:p-6">
      {offerBrief && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-brand/40 bg-brand/10 p-3"
             data-testid="brief-offer">
          <img src="/milu-wave.png" alt="" className="h-10 w-10 shrink-0 object-contain" />
          <p className="min-w-0 flex-1 text-sm">Good morning — shall I read you the day?</p>
          <button
            data-testid="brief-offer-play"
            onClick={() => { dismissOffer(); speakBrief("/home/brief/say"); }}
            className="inline-flex min-h-[38px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95"
          >
            <Radio className="h-3.5 w-3.5" /> Yes please
          </button>
          <button
            data-testid="brief-offer-dismiss"
            onClick={dismissOffer}
            className="text-xs text-ink-soft underline decoration-dotted hover:text-ink"
          >
            Not now
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
          <CloudSun className="h-4 w-4 text-sky-300" /> Tomorrow
        </p>
        {data.place?.name && (
          <span className="inline-flex items-center gap-1 text-xs text-ink-soft" data-testid="home-weather-place">
            <MapPin className="h-3 w-3" /> {data.place.name}
          </span>
        )}
      </div>

      {/* One tap each: the day, the week, tomorrow's sky — sized for a thumb on a phone. */}
      <div className="mt-3 flex flex-wrap gap-2" data-testid="brief-buttons">
        <BriefButton path="/home/brief/say" label="Brief me" icon={Radio} primary
                     testid="home-brief-listen" />
        <BriefButton path="/home/week/say" label={sundayEvening ? "Week ahead — ready" : "Week ahead"}
                     icon={CalendarRange} primary={sundayEvening} testid="home-week-listen" />
        <ShowButton label="Today's headlines" icon={Newspaper} on={panel === "news"}
                    onClick={() => show("news")} testid="home-headlines-toggle" />
        <ShowButton label="While you slept" icon={Sunrise} on={panel === "overnight"}
                    onClick={() => show("overnight")} testid="home-overnight-toggle" />
        <ShowButton label="Your Personal Preferences" icon={SlidersHorizontal} on={panel === "prefs"}
                    onClick={() => show("prefs")} testid="home-prefs-toggle" />
        <ShowButton label="Your Daily Digest" icon={Newspaper} on={panel === "digest"}
                    onClick={() => show("digest")} testid="home-digest-toggle" />
        {data.sky && <BriefButton path="/home/weather/say" label="Hear it" icon={Volume2}
                                  testid="home-weather-listen" />}
      </div>

      {panel === "overnight" && (
        <div className="mt-4" data-testid="home-overnight-slot">
          <OvernightCard speaker={speaker} lang={briefLang || lang} />
        </div>
      )}

      {panel === "news" && <NewsPanel news={news} onTopics={loadNews} speaker={speaker} bare />}

      {panel === "prefs" && (
        <PrefsPanel
          languages={languages}
          voice={voice}
          onVoice={pickVoice}
          readLang={briefLang || lang}
          onReadLang={pickLanguage}
        />
      )}

      {panel === "digest" && <DigestPanel speaker={speaker} lang={briefLang || lang} />}

      <BriefHistory speaker={speaker} lang={briefLang || lang} />
      <TomorrowSky data={data} moving={moving} onMove={moveIt} />
      <WeekendSlot weekend={weekend} planning={planning} onPlan={planIt} />
      <EveningCard speaker={speaker} lang={briefLang || lang} />
      <RecapCard speaker={speaker} />

      {data.stale && (
        <p className="mt-3 text-xs text-ink-soft" data-testid="home-weather-stale">
          Last known forecast — the weather service is busy.
        </p>
      )}
    </div>
  );
};
